// src/pages/break/BreakTimeLogPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchBreakLogs,
  createBreakLog,
  punchOutBreakLog,
  deleteBreakLog,
  exportBreakLogs, // ← NEW
  clearError,
  fetchBreakLogsHide,
} from "store/slices/breakTimeLogSlice";
import { Loader2, Clock, Trash2, AlertCircle } from "lucide-react";
import DataTable from "../../components/common/DataTable";
import PageHeader from "../../components/common/PageHeader";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "../../components/ui/alert-dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";
import { usePermissions } from "hooks/usePermissions";
import usePermissionSocket from "hooks/usePermissionSocket";
import profileApi from "api/profileApi";
import { cn } from "lib/utils";
import { formatDateTime } from "utlis/formateDateTime";

const BREAK_TYPES = [
  { value: "morning_break", label: "Morning Break" },
  { value: "lunch_break", label: "Lunch Break" },
  { value: "evening_break", label: "Evening Break" },
  { value: "dinner_break", label: "Dinner Break" },
  { value: "break_not_availed", label: "Break Not Availed" },
  { value: "not_on_desk_break", label: "Not On Desk Break" },
];

const BREAK_NOT_TAKEN_OPTIONS = [
  { value: "all", label: "All" },
  { value: "morning_break", label: "Morning Break" },
  { value: "lunch_break", label: "Lunch Break" },
  { value: "evening_break", label: "Evening Break" },
  { value: "dinner_break", label: "Dinner Break" },
];

const BreakTimeLogPage = () => {
  usePermissionSocket();
  const dispatch = useDispatch();

  const {
    logs = [],
    pagination = { count: 0, page_size: 10 },
    isLoading,
    error,
  } = useSelector((state) => state.breakTimeLog || {});

  // Updated permissions: include export
  const {
    canCreate,
    canUpdate,
    canDelete,
    canRead,
    canExport = false,
  } = usePermissions("break_time_log");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [lunchDuration, setLunchDuration] = useState("00:30:00");
  const [shiftTime, setShiftTime] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({
    title: "",
    not_on_desk_reason: "",
    break_not_availed: "",
  });
  const [form, setForm] = useState({
    title: "",
    not_on_desk_reason: "",
    break_not_availed: "",
  });

  const { attendanceLogs } = useSelector((state) => state.breakTimeLog);
  const { user } = useSelector((state) => state.auth);
  const currentEmployeeId = user?.id;

  // Find active break that belongs to the CURRENT user only
  const myActiveBreak = logs.find(
    (log) =>
      !log.punch_out &&
      log.title !== "break_not_availed" &&
      log.employee === currentEmployeeId
  );
  const hasMyActiveBreak = !!myActiveBreak;

  // Load lunch duration from profile
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const profile = await profileApi.getMyProfile();
        setLunchDuration(profile.lunch_duration || "00:30:00");
        setShiftTime(profile.shift_start_time);
      } catch (err) {
        console.error("Failed to load profile lunch duration");
      }
    };
    loadProfile();
  }, []);

  // Primary fetch (exclude attendance)
  const refetch = useCallback(() => {
    dispatch(
      fetchBreakLogs({
        page,
        pageSize,
        search: searchTerm,
        exclude_title: "attendance",
      })
    );
  }, [dispatch, page, pageSize, searchTerm]);

  // Always call primary fetch
  useEffect(() => {
    refetch();
  }, [refetch]);

  // Secondary fetch (attendance only)
  const fetchAttendance = useCallback(() => {
    dispatch(
      fetchBreakLogsHide({
        page: 1,
        pageSize: 1,
        search: searchTerm,
        employee: currentEmployeeId,
        title: "attendance",
        date_range: "today",
      })
    );
  }, [dispatch, searchTerm, currentEmployeeId]);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  // Handle errors
  useEffect(() => {
    if (error) {
      toast.error(typeof error === "string" ? error : "An error occurred");
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const today = new Date().toISOString().slice(0, 10);
  const now = new Date();
  const currentTime = now.toTimeString().slice(0, 8);

  const takenToday = logs
    .filter(
      (l) =>
        l.employee === currentEmployeeId &&
        l.punch_in &&
        new Date(l.punch_in).toISOString().slice(0, 10) === today &&
        l.punch_out
    )
    .map((l) => l.title);

  const isStandardBreakTaken = (type) => {
    return type !== "not_on_desk_break" && takenToday.includes(type);
  };

  const getAvailableBreakNotAvailedOptions = () => {
    // 1. Check if "All" break not availed already exists
    const hasAllNotAvailed = logs.some(
      (l) =>
        l.employee === currentEmployeeId &&
        l.title === "break_not_availed" &&
        l.break_not_availed === "all" &&
        new Date(l.created_at).toISOString().slice(0, 10) === today
    );

    // If "All" exists, disable everything
    if (hasAllNotAvailed) {
      return BREAK_NOT_TAKEN_OPTIONS.map((opt) => ({
        ...opt,
        disabled: true,
        disabledReason: "",
      }));
    }

    // 2. If "All" doesn't exist, check individual breaks
    return BREAK_NOT_TAKEN_OPTIONS.map((opt) => {
      let isDisabled = false;
      let reason = "";

      // Helper to check if a specific break type is handled (Taken OR Not Availed)
      const isBreakHandled = (type) => {
        const taken = isStandardBreakTaken(type);
        const notAvailed = logs.some(
          (l) =>
            l.employee === currentEmployeeId &&
            l.title === "break_not_availed" &&
            l.break_not_availed === type &&
            new Date(l.created_at).toISOString().slice(0, 10) === today
        );
        return taken || notAvailed;
      };

      if (opt.value === "all") {
        const anyBreakHandled = [
          "morning_break",
          "lunch_break",
          "evening_break",
          "dinner_break",
        ].some(isBreakHandled);

        if (anyBreakHandled) {
          isDisabled = true;
        }
      }
      else {
        // Disable specific option (e.g., "Morning Break") if:
        // A. The actual break was taken today
        // B. "Break Not Availed" for "Morning Break" was already submitted today
        const actuallyTaken = isStandardBreakTaken(opt.value);
        const notAvailedSubmitted = logs.some(
          (l) =>
            l.employee === currentEmployeeId &&
            l.title === "break_not_availed" &&
            l.break_not_availed === opt.value &&
            new Date(l.created_at).toISOString().slice(0, 10) === today
        );

        if (actuallyTaken || notAvailedSubmitted) {
          isDisabled = true;
          reason = actuallyTaken ? "" : "";
        }
      }

      return { ...opt, disabled: isDisabled, disabledReason: reason };
    });
  };

  const isBreakTypeDisabled = (type) => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const currentMinutes = hours * 60 + minutes;

    // Define cutoff times in minutes from midnight
    const MORNING_CUTOFF = 12 * 60 + 30; // 12:30 PM (750 minutes)
    const LUNCH_CUTOFF = 15 * 60 + 30;   // 3:30 PM (930 minutes)
    const EVENING_CUTOFF = 17 * 60 + 45; // 5:45 PM (1065 minutes)

    switch (type) {
      case "morning_break":
        return currentMinutes >= MORNING_CUTOFF;
      case "lunch_break":
        return currentMinutes >= LUNCH_CUTOFF;
      case "evening_break":
        return currentMinutes >= EVENING_CUTOFF;
      default:
        return false; // dinner_break & not_on_desk_break never disabled by time
    }
  };

  const getDurationSeconds = (start, end) => {
    if (!start || !end) return 0;
    return (new Date(end) - new Date(start)) / 1000;
  };

  const getAvailableBreakTypes = () => {
    if (!shiftTime) return BREAK_TYPES; // fallback if not loaded

    const [hours] = shiftTime.split(":").map(Number);
    const shiftStartHour = hours;

    // If shift starts at or after 12:00 PM (noon), hide Morning Break
    if (shiftStartHour >= 12) {
      return BREAK_TYPES.filter((bt) => bt.value !== "morning_break");
    }

    return BREAK_TYPES;
  };

  const formatDuration = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (hours > 0 || minutes > 0) parts.push(`${minutes}m`);
    parts.push(`${seconds}s`);
    return parts.join(" ");
  };

  const resetForm = () => {
    setForm({
      title: "",
      not_on_desk_reason: "",
      break_not_availed: "",
    });

    setErrors({
      title: "",
      not_on_desk_reason: "",
      break_not_availed: "",
    });
  };

  const handleStartBreak = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({
      title: "",
      not_on_desk_reason: "",
      break_not_availed: "",
    });

    let hasError = false;

    // Validate break type
    if (!form.title) {
      setErrors((prev) => ({
        ...prev,
        title: "Please select a break type",
      }));
      hasError = true;
    }

    // Validate not on desk reason
    if (
      form.title === "not_on_desk_break" &&
      !form.not_on_desk_reason.trim()
    ) {
      setErrors((prev) => ({
        ...prev,
        not_on_desk_reason: "Reason is required",
      }));
      hasError = true;
    }

    if (form.title === "break_not_availed" && !form.break_not_availed) {
      setErrors((prev) => ({
        ...prev,
        break_not_availed: "Please select an option",
      }));
      hasError = true;
    }

    // if (hasError) return;
    if (hasError) {
      setIsSubmitting(false);
      return;
    }

    try {
      const payload = {
        title: form.title,

        // not_on_desk_reason:
        //   form.title === "not_on_desk_break"
        //     ? form.not_on_desk_reason
        //     : null,

          not_on_desk_reason:
          form.title === "break_not_availed"
            ? "Break Not Availed"
            : form.title === "not_on_desk_break"
              ? form.not_on_desk_reason
              : null,

        break_not_availed:
          form.title === "break_not_availed"
            ? form.break_not_availed
            : null,

      };

      // ✅ Only send punch_in/punch_out for actual breaks
      if (form.title !== "break_not_availed") {
        payload.punch_in = new Date().toISOString();
        payload.punch_out = null;
      }

      await dispatch(createBreakLog(payload)).unwrap();

      // toast.success("Break started");
      toast.success(
        form.title === "break_not_availed"
          ? "Record submitted"
          : "Break started"
      );

      setIsDialogOpen(false);

      resetForm();

      setTimeout(() => {
        refetch();
      }, 500);
    } catch (err) {
      console.log("err", err);
      toast.error(err.error);
    }
    finally {
      setIsSubmitting(false);
    }
  };

  const handlePunchOut = async (id) => {
    try {
      await dispatch(
        punchOutBreakLog({ id, punch_out: new Date().toISOString() })
      ).unwrap();
      toast.success("Punched out successfully");
      setTimeout(() => {
        refetch();
      }, 500);
    } catch {
      toast.error("Failed to punch out");
    }
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteBreakLog(deleteId)).unwrap();
      toast.success("Deleted");
      setIsDeleteOpen(false);
      refetch();
    } catch { }
  };

  const handleExport = () => {
    if (!canExport) {
      toast.error("You don't have permission to export");
      return;
    }
    dispatch(exportBreakLogs({ exclude_title: 'attendance' }));
    toast.success("Export started... downloading CSV");
  };

  const columns = [
    {
      accessorKey: "employee_name",
      header: "Employee Name",
      cell: ({ row }) => (
        <span className="font-medium">{row.original.employee_name ?? "-"}</span>
      ),
    },
    {
      accessorKey: "company_name",
      header: "Company Name",
      cell: ({ row }) => (
        <span className="font-medium">{row.original.company_name ?? "-"}</span>
      ),
    },
    {
      accessorKey: "title_display",
      header: "Break Type",
      cell: ({ row }) => (
        <Badge variant="outline">{row.original.title_display}</Badge>
      ),
    },
    {
      accessorKey: "punch_in",
      header: "Punch In",
      cell: ({ row }) => {
        const log = row.original;

        if (log.title === "break_not_availed") {
          return "-";
        }

        return formatDateTime(log.punch_in) ?? "-";
      },
    },
    {
      accessorKey: "punch_out",
      header: "Punch Out",
      cell: ({ row }) => {
        const log = row.original;

        if (log.title === "break_not_availed") {
          return "-";
        }

        return log.punch_out ? (
          formatDateTime(log.punch_out) ?? "-"
        ) : (
          <Badge variant="secondary">Active</Badge>
        );
      },
    },
    {
      header: "Break Duration",
      cell: ({ row }) => {
        const log = row.original;
        if (log.title === "break_not_availed") {
          return "-";
        }
        if (!log.punch_out) return <Badge variant="secondary">Ongoing</Badge>;

        const durationSec = getDurationSeconds(log.punch_in, log.punch_out);

        // Define allowed duration per break type
        const getAllowedSeconds = (title) => {
          if (title === "lunch_break") {
            const [h, m] = lunchDuration.split(":").map(Number);
            return h * 3600 + m * 60; // e.g., 00:30:00 → 1800 sec
          }
          if (title === "morning_break" || title === "evening_break") {
            return 10 * 60; // 10 minutes = 600 seconds
          }
          return null; // No limit for not_on_desk_break
        };

        const allowedSeconds = getAllowedSeconds(log.title);
        const exceeded =
          allowedSeconds !== null && durationSec > allowedSeconds;

        const formatted = formatDuration(durationSec);

        if (!allowedSeconds) {
          return (
            <span
              className={cn(
                "text-foreground font-medium",
                exceeded ? "text-red-600" : "text-green-600"
              )}
            >
              {formatted}
            </span>
          );
        }

        return (
          <div className="flex items-center gap-2">
            <span
              className={cn(
                "font-medium",
                exceeded ? "text-red-600" : "text-green-600"
              )}
            >
              {formatted}
            </span>
            {exceeded && (
              <AlertCircle
                className="h-4 w-4 text-red-600"
                title="Exceeded allowed time"
              />
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "break_not_availed",
      header: "Break Not Availed",
      cell: ({ row }) => row.original.break_not_availed_display || "-",
    },
    {
      accessorKey: "reason",
      header: "Reason",
      cell: ({ row }) => {
        const log = row.original;

        // if (log.title === "break_not_availed") {
        //   return (
        //     row.original.title_display ?? "-"
        //   );
        // }

        return log.not_on_desk_reason || "-";
      },
    },
    {
      id: "actions",
      header: "Actions",

      cell: ({ row }) => {

        const log = row.original;
        const isMyLog = log.employee === currentEmployeeId;
        const isActive = !log.punch_out;
        return (
          <div className="flex gap-2">
            {canDelete && (
              <Button
                size="sm"
                variant="destructive"
                onClick={() => {
                  setDeleteId(log.id);
                  setIsDeleteOpen(true);
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">

      <PageHeader
        title="Break Time Logs"
        description="Track breaks with auto punch-in/out and duration alerts"
        action={
          canCreate && !hasMyActiveBreak && (attendanceLogs.length > 0 || isLoading)
            ? { label: "Start Break", onClick: () => setIsDialogOpen(true) }
            : null
        }
      />

      {hasMyActiveBreak && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg flex items-center justify-between">
          <div>
            You are on an active break:{" "}
            <strong>{myActiveBreak.title_display}</strong> (Started at{" "}
            {new Date(myActiveBreak.punch_in).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
            )
          </div>
          {canUpdate && (
            <Button
              size="sm"
              onClick={() => handlePunchOut(myActiveBreak.id)}
              className="ml-4"
            >
              <Clock className="h-4 w-4 mr-1" />
              Punch Out Now
            </Button>
          )}
        </div>
      )}

      <DataTable
        data={logs}
        columns={columns}
        totalCount={pagination.count}
        searchPlaceholder="Search employee or reason..."
        pageIndex={page - 1}
        pageSize={pageSize}
        pageCount={Math.ceil(pagination.count / pagination.page_size)}
        onPageChange={(i) => setPage(i + 1)}
        onPageSizeChange={setPageSize}
        onSearch={setSearchTerm}
        isLoading={isLoading}
        showExport={canExport}
        onExport={handleExport}
        showImport={false}
      />

      {/* START BREAK DIALOG */}
      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            resetForm();
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Start a Break</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleStartBreak} className="space-y-4">
            {/* BREAK TYPE SELECT */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Break Type <span className="text-destructive">*</span>
              </label>

              <Select
                value={form.title}
                onValueChange={(v) => {
                  setForm({ ...form, title: v, not_on_desk_reason: "", break_not_availed: "" });
                  setErrors({ ...errors, title: "" });
                }}
              >
                <SelectTrigger
                  className={cn(
                    "w-full",
                    errors.title && "border-red-500 focus:ring-red-500"
                  )}
                >
                  <SelectValue placeholder="Select break type" />
                </SelectTrigger>

                <SelectContent>
                  {getAvailableBreakTypes().map((bt) => {
                    const takenToday = isStandardBreakTaken(bt.value);
                    const disabledByTime = isBreakTypeDisabled(bt.value);
                    const isDisabled = takenToday || disabledByTime;

                    return (
                      <SelectItem
                        key={bt.value}
                        value={bt.value}
                        disabled={isDisabled}
                        className={isDisabled ? "text-muted-foreground" : ""}
                      >
                        <div className="flex items-center justify-between w-full">
                          <span>{bt.label}</span>
                          {isDisabled && (
                            <span className="text-xs text-muted-foreground">
                              {takenToday ? "" : disabledByTime ? "" : ""}
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>

              {errors.title && (
                <p className="text-sm text-destructive">{errors.title}</p>
              )}
            </div>

            {/* NOT ON DESK REASON */}
            {form.title === "not_on_desk_break" && (
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Reason <span className="text-destructive">*</span>
                </label>
                <textarea
                  className={cn(
                    "w-full px-3 py-2 border rounded-md resize-none",
                    errors.not_on_desk_reason &&
                    "border-red-500 focus:ring-red-500"
                  )}
                  rows={3}
                  placeholder="e.g., Client meeting, Doctor visit..."
                  value={form.not_on_desk_reason}
                  onChange={(e) => {
                    setForm({ ...form, not_on_desk_reason: e.target.value });
                    setErrors({ ...errors, not_on_desk_reason: "" });
                  }}
                />
                {errors.not_on_desk_reason && (
                  <p className="text-sm text-destructive">
                    {errors.not_on_desk_reason}
                  </p>
                )}
              </div>
            )}

            {/* BREAK NOT TAKEN REASON */}
            {form.title === "break_not_availed" && (
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Select Option <span className="text-destructive">*</span>
                </label>

                <Select
                  value={form.break_not_availed}
                  onValueChange={(v) => {
                    setForm({
                      ...form,
                      break_not_availed: v,
                    });

                    setErrors({
                      ...errors,
                      break_not_availed: "",
                    });
                  }}
                >
                  <SelectTrigger
                    className={cn(
                      "w-full",
                      errors.break_not_availed &&
                      "border-red-500 focus:ring-red-500"
                    )}
                  >
                    <SelectValue placeholder="Select option" />
                  </SelectTrigger>

                  <SelectContent>
                    {getAvailableBreakNotAvailedOptions().map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        disabled={option.disabled}
                        className={option.disabled ? "text-muted-foreground opacity-70" : ""}
                      >
                        <div className="flex items-center justify-between w-full">
                          <span>{option.label}</span>
                          {option.disabled && (
                            <span className="text-xs text-muted-foreground">
                              {option.disabledReason || ""}
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {errors.break_not_availed && (
                  <p className="text-sm text-destructive">
                    {errors.break_not_availed}
                  </p>
                )}
              </div>
            )}

            {form.title !== "break_not_availed" &&
              <div className="text-sm text-muted-foreground">
                Break punch-in time will be set automatically to{" "}
                <strong>{currentTime}</strong>
              </div>}

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  resetForm();
                  setIsDialogOpen(false);
                }}
              >
                Cancel
              </Button>
              {/* <Button type="submit">Start Break</Button> */}
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {form.title === "break_not_availed"
                      ? "Submitting..."
                      : "Starting..."}
                  </div>
                ) : form.title === "break_not_availed" ? (
                  "Submit"
                ) : (
                  "Start Break"
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* DELETE CONFIRM */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Break Log?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BreakTimeLogPage;
